import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../../css/dashboard.css"
import axios from 'axios';
import CommonTable from "../component/table_list/common_table";
import CommonTableRow from "../component/table_list/common_tablerow";
import CommonTableColumn from "../component/table_list/common_tablecolumn";
import Pagination from "react-js-pagination";
import styled from "styled-components"
import { formatDate } from "../utils/format_date"
import { useAuthContext } from '../common/ProtectedLayout';

function PointUsage(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const [isValidAccess, setIsValidAccess] = useState(false);

    const [usageList, setUsageList] = useState([]);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const items = 10;

    const auth = useAuthContext();

    // 모달 관련
    const modalRef = useRef();
    const orderListModalRef = useRef();
    const [selectedItem, setSelectedItem] = useState(null);
    const [orderList, setOrderList] = useState([]);

    const handlePageChange = (page) => { setPage(page) };

    const fetchUsageList = async () => {
        try {
            const offset = (page - 1) * 10;
            const params = searchQuery.trim() !== '' ? { query: searchQuery } : {};
            const result = await axios.get(`/PointUsage/${offset}`, { params });
            if (result.data.success) {
                setUsageList(result.data.message);
            }
        } catch (error) {
            console.error('Error fetching point usage:', error.message);
        }
    };

    const fetchTotalCount = async () => {
        try {
            const params = searchQuery.trim() !== '' ? { query: searchQuery } : {};
            const result = await axios.get('/PointUsageCount', { params });
            if (result.data.success) {
                setTotalCount(result.data.message);
            }
        } catch (error) {
            console.error('Error fetching count:', error.message);
        }
    };

    const handleSearch = () => {
        setPage(1);
        fetchUsageList();
        fetchTotalCount();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleRowClick = (item) => {
        setSelectedItem(item);
        modalRef.current?.showModal();
    };

    const handleCloseModal = () => {
        setSelectedItem(null);
        modalRef.current?.close();
    };

    const handleViewReservation = (reqNumber) => {
        if (!reqNumber) return;

        // 쉼표로 구분된 여러 예약번호 처리
        const orders = reqNumber.split(',').map(s => s.trim()).filter(s => s !== '');

        if (orders.length > 1) {
            // 여러 개일 경우 선택 모달 표시
            setOrderList(orders);
            orderListModalRef.current?.showModal();
        } else {
            // 하나일 경우 바로 이동
            handleCloseModal();
            navigate('/listdetail', { state: { req_number: orders[0], beforePage: '/point_usage' } });
        }
    };

    const handleOrderSelect = (reqNumber) => {
        setOrderList([]);
        orderListModalRef.current?.close();
        handleCloseModal();
        navigate('/listdetail', { state: { req_number: reqNumber, beforePage: '/point_usage' } });
    };

    const handleCloseOrderListModal = () => {
        setOrderList([]);
        orderListModalRef.current?.close();
    };

    useEffect(() => {
        fetchUsageList();
    }, [page]);

    useEffect(() => {
        setIsValidAccess(true);
        fetchUsageList();
        fetchTotalCount();
    }, [location.state, navigate]);

    if (!isValidAccess) {
        return null;
    }

    const formatPoint = (point) => {
        if (!point && point !== 0) return '-';
        return new Intl.NumberFormat('ko-KR').format(point) + 'P';
    };

    return (
        <div className="dashboard-container">
            <div className="content-container">
                <SearchContainer>
                    <SearchInput
                        type="text"
                        placeholder="예약번호, 이메일, 고객명 검색"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={handleKeyPress}
                    />
                    <SearchButton onClick={handleSearch}>검색</SearchButton>
                </SearchContainer>

                <CommonTable headerTitle={['번호', '예약번호', '사용자', '연락처', '사용포인트', '사용일시']}>
                    {usageList && usageList.length > 0 ? (
                        usageList.map((item, index) => (
                            <CommonTableRow key={item.idx || index} kind="notDetail" onClick={() => handleRowClick(item)}>
                                <CommonTableColumn>{totalCount - ((page - 1) * items) - index}</CommonTableColumn>
                                <CommonTableColumn>{item.req_number || '-'}</CommonTableColumn>
                                <CommonTableColumn>{item.user_name || item.email_id || '-'}</CommonTableColumn>
                                <CommonTableColumn>{item.phone || '-'}</CommonTableColumn>
                                <CommonTableColumn>{formatPoint(item.used_point)}</CommonTableColumn>
                                <CommonTableColumn>{formatDate(item.used_date)}</CommonTableColumn>
                            </CommonTableRow>
                        ))
                    ) : (
                        <CommonTableRow>
                            <CommonTableColumn colSpan="6">
                                <EmptyMessage>포인트 사용 내역이 없습니다.</EmptyMessage>
                            </CommonTableColumn>
                        </CommonTableRow>
                    )}
                </CommonTable>

                <PaginationBox>
                    <Pagination
                        activePage={page}
                        itemsCountPerPage={items}
                        totalItemsCount={totalCount}
                        pageRangeDisplayed={5}
                        onChange={handlePageChange}
                    />
                </PaginationBox>
            </div>

            {/* 상세 모달 */}
            <Modal ref={modalRef} onClose={handleCloseModal}>
                <ModalHeader>포인트 사용 상세</ModalHeader>
                <ModalBody>
                    <DetailRow>
                        <DetailLabel>사용자 이메일</DetailLabel>
                        <DetailValue>{selectedItem?.email_id || '-'}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                        <DetailLabel>사용자명</DetailLabel>
                        <DetailValue>{selectedItem?.user_name || '-'}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                        <DetailLabel>연락처</DetailLabel>
                        <DetailValue>{selectedItem?.phone || '-'}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                        <DetailLabel>사용포인트</DetailLabel>
                        <DetailValue style={{ color: '#006edc', fontWeight: 'bold' }}>
                            {formatPoint(selectedItem?.used_point)}
                        </DetailValue>
                    </DetailRow>
                    <DetailRow>
                        <DetailLabel>사용일시</DetailLabel>
                        <DetailValue>{formatDate(selectedItem?.used_date)}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                        <DetailLabel>예약번호</DetailLabel>
                        <DetailValueWithButton>
                            <span>{selectedItem?.req_number || '-'}</span>
                            {selectedItem?.req_number && (
                                <ViewButton onClick={() => handleViewReservation(selectedItem.req_number)}>
                                    보기
                                </ViewButton>
                            )}
                        </DetailValueWithButton>
                    </DetailRow>
                    <ModalFooter>
                        <ConfirmButton onClick={handleCloseModal}>확인</ConfirmButton>
                    </ModalFooter>
                </ModalBody>
            </Modal>

            {/* 예약번호 선택 모달 */}
            <OrderListModal ref={orderListModalRef} onClose={handleCloseOrderListModal}>
                <OrderListHeader>예약번호 선택</OrderListHeader>
                <OrderListBody>
                    <OrderListDescription>상세 정보를 볼 예약을 선택해주세요.</OrderListDescription>
                    <OrderListContainer>
                        {orderList.map((num, idx) => (
                            <OrderButton key={idx} onClick={() => handleOrderSelect(num)}>
                                {num}
                            </OrderButton>
                        ))}
                    </OrderListContainer>
                    <OrderListCloseButton onClick={handleCloseOrderListModal}>닫기</OrderListCloseButton>
                </OrderListBody>
            </OrderListModal>
        </div>
    );
}

export default PointUsage;

// Styled Components
const SearchContainer = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 20px;
    margin-bottom: 20px;
    margin-left: 5%;
    width: auto;
    max-width: 500px;
`;

const SearchInput = styled.input`
    flex: 1;
    padding: 12px 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 15px;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #006edc;
    }

    &::placeholder {
        color: #999;
    }
`;

const SearchButton = styled.button`
    padding: 12px 24px;
    background-color: #006edc;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);

    &:hover {
        background-color: #0056b3;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
`;

const EmptyMessage = styled.div`
    text-align: center;
    color: #666;
    padding: 40px 0;
`;

const PaginationBox = styled.div`
    margin-top: 20px;

    .pagination {
        display: flex;
        justify-content: center;
        margin-top: 15px;
    }

    ul {
        list-style: none;
        padding: 0;
    }

    ul.pagination li {
        display: inline-block;
        width: 30px;
        height: 30px;
        border: 1px solid #e2e2e2;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 1rem;
    }

    ul.pagination li:first-child {
        border-radius: 5px 0 0 5px;
    }

    ul.pagination li:last-child {
        border-radius: 0 5px 5px 0;
    }

    ul.pagination li a {
        text-decoration: none;
        color: #337ab7;
        font-size: 1rem;
    }

    ul.pagination li.active a {
        color: white;
    }

    ul.pagination li.active {
        background-color: #337ab7;
    }

    ul.pagination li a:hover,
    ul.pagination li a.active {
        color: blue;
    }
`;

// Modal Styled Components
const Modal = styled.dialog`
    padding: 20px;
    border: none;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    min-width: 400px;
    max-width: 500px;

    &::backdrop {
        background: rgba(0, 0, 0, 0.5);
    }
`;

const ModalHeader = styled.h3`
    margin: 0 0 20px 0;
    font-size: 1.1rem;
    font-weight: bold;
    color: #333;
    text-align: center;
    padding-bottom: 15px;
    border-bottom: 1px solid #eee;
`;

const ModalBody = styled.div`
    padding: 0;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #eee;
`;

const ConfirmButton = styled.button`
    padding: 10px 40px;
    background-color: #006edc;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: #0056b3;
    }
`;

const DetailRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
        border-bottom: none;
    }
`;

const DetailLabel = styled.span`
    font-weight: 600;
    color: #555;
    font-size: 0.95rem;
    width: 35%;
`;

const DetailValue = styled.span`
    color: #333;
    font-size: 0.95rem;
    width: 65%;
    text-align: right;
`;

const DetailValueWithButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    width: 65%;

    span {
        color: #333;
        font-size: 0.95rem;
    }
`;

const ViewButton = styled.button`
    padding: 4px 12px;
    background-color: #006edc;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.85rem;
    font-weight: bold;
    transition: background-color 0.2s;

    &:hover {
        background-color: #0056b3;
    }
`;

// Order List Modal Styled Components (biling.js 패턴 적용)
const OrderListModal = styled.dialog`
    padding: 20px;
    border: none;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    min-width: 300px;
    text-align: center;

    &::backdrop {
        background: rgba(0, 0, 0, 0.5);
    }
`;

const OrderListHeader = styled.h3`
    margin: 0 0 20px 0;
    font-size: 1.1rem;
    font-weight: bold;
    color: #333;
`;

const OrderListBody = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const OrderListDescription = styled.p`
    margin: 0 0 20px 0;
    font-size: 0.9rem;
    color: #666;
`;

const OrderListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 300px;
    overflow-y: auto;
    width: 100%;
`;

const OrderButton = styled.button`
    padding: 12px;
    background-color: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    color: #333;
    font-weight: bold;
    cursor: pointer;
    font-size: 1rem;
    transition: all 0.2s;

    &:hover {
        background-color: #f0f8ff;
        border-color: #006edc;
        color: #006edc;
    }
`;

const OrderListCloseButton = styled.button`
    margin-top: 20px;
    padding: 8px 20px;
    background-color: #eee;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.9rem;
    color: #555;
    transition: background-color 0.2s;

    &:hover {
        background-color: #ddd;
    }
`;
